import React from 'react';
import { NavLink } from 'react-router-dom';
import { heroContent, services, slogans, additionalServices, companyData } from '../data/content';
import ServiceCard from '../components/ui/ServiceCard';
import SafeIcon from '../common/SafeIcon';
import { FiCheck, FiArrowRight, FiPhone, FiStar, FiSettings } from 'react-icons/fi';

const Home = () => {
  // PNG Logo für die Startseite
  const logoUrl = "https://canv.wasacon.com/ms-terassen-de/_assets/media/c4a6307fc61642f7a858d7ac2ba52c5a.png";

  return (
    <div className="overflow-hidden bg-white">
      {/* Hero Section */}
      <section className="relative pt-12 pb-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 text-center">
          
          {/* Logo für Startseite - nochmals um 50% vergrößert */}
          <div className="flex justify-center mb-10">
            <img src={logoUrl} alt="Oscar Terrassen" className="h-72 md:h-[27rem] w-auto object-contain" />
          </div>

          <div className="max-w-4xl mx-auto mb-12">
            <h2 className="text-ms-red font-black uppercase tracking-[0.25em] text-sm mb-6">
              Ihr zertifizierter Fachbetrieb aus Heinsberg
            </h2>
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-black text-ms-dark leading-[1.1] uppercase tracking-tighter mb-8 break-words">
              {heroContent.headlines[0]}
            </h1>
            <p className="text-lg md:text-xl text-ms-dark/60 font-medium max-w-2xl mx-auto break-words">
              {heroContent.subheadlines[0]}
            </p>
          </div>

          {/* 3 Buttons in Hero */}
          <div className="flex flex-col md:flex-row gap-4 justify-center items-center">
            <NavLink
              to="/konfigurator"
              className="w-full md:w-auto bg-ms-blue text-white px-8 py-5 rounded-2xl font-black text-lg transition-all shadow-xl hover:-translate-y-1 uppercase tracking-wider flex items-center justify-center gap-3"
            >
              <SafeIcon icon={FiSettings} />
              Zum Konfigurator
            </NavLink>
            <NavLink
              to="/kontakt"
              className="w-full md:w-auto bg-gradient-to-r from-ms-red to-[#ff4d4d] text-white px-8 py-5 rounded-2xl font-black text-lg transition-all shadow-xl hover:-translate-y-1 uppercase tracking-wider flex items-center justify-center gap-3"
            >
              Angebot anfragen <SafeIcon icon={FiArrowRight} />
            </NavLink>
            <a
              href={`tel:${companyData.phone.replace(/\s/g, '')}`}
              className="w-full md:w-auto bg-ms-solar text-white px-8 py-5 rounded-2xl font-black text-lg hover:bg-ms-blue transition-all shadow-xl hover:-translate-y-1 uppercase tracking-wider flex items-center justify-center gap-3"
            >
              <SafeIcon icon={FiPhone} />
              Direkt anrufen
            </a>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-24 bg-ms-light/30">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <span className="text-ms-blue font-black uppercase tracking-[0.3em] text-sm">Unsere Leistungen</span>
            <h2 className="text-4xl md:text-5xl font-black text-ms-dark mt-4 uppercase tracking-tighter">Produkte für Ihr Zuhause</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((service) => (
              <ServiceCard key={service.id} title={service.title} desc={service.shortDesc} icon={service.icon} link={`/leistungen#${service.id}`} />
            ))}
          </div>
        </div>
      </section>

      {/* Why Us / Benefits */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-ms-red font-black uppercase tracking-[0.3em] text-sm">Warum wir?</span>
              <h2 className="text-4xl md:text-5xl font-black text-ms-dark mt-4 mb-8 uppercase tracking-tighter leading-tight break-words">
                Qualität, die Ihr Zuhause veredelt.
              </h2>
              <div className="space-y-6">
                {slogans.map((benefit, i) => (
                  <div key={i} className="flex items-start">
                    <div className="bg-ms-blue/10 p-2 rounded-lg mr-4 mt-1 flex-shrink-0">
                      <SafeIcon icon={FiCheck} className="text-ms-blue font-bold" />
                    </div>
                    <p className="text-ms-dark/80 font-bold uppercase text-sm tracking-tight break-words hyphens-auto">{benefit}</p>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Responsive Grid for Additional Services */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {additionalServices.map((s, i) => (
                <div key={i} className="bg-ms-light p-6 md:p-8 rounded-[2rem] border border-ms-blue/5 flex flex-col h-full overflow-hidden">
                  <SafeIcon icon={s.icon} className="text-3xl text-ms-blue mb-4 flex-shrink-0" />
                  <h4 className="font-black text-ms-dark uppercase tracking-tighter mb-2 break-words leading-tight">{s.title}</h4>
                  <p className="text-xs text-ms-dark/60 font-bold uppercase break-words hyphens-auto leading-relaxed">{s.desc}</p>
                </div>
              ))}
              <div className="bg-ms-solar p-6 md:p-8 rounded-[2rem] text-white flex flex-col justify-center items-center text-center shadow-lg border border-ms-blue/20">
                <SafeIcon icon={FiStar} className="text-4xl mb-4 text-ms-blue" />
                <p className="font-black uppercase tracking-tighter text-sm break-words">Regionale Meisterschmiede</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 bg-brand-gradient text-white text-center">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-4xl md:text-6xl font-black mb-8 uppercase tracking-tighter break-words">Bereit für Ihr Projekt?</h2>
          <p className="text-xl mb-12 font-medium opacity-90 break-words">
            Beschreiben Sie uns Ihr Vorhaben oder nutzen Sie unseren Konfigurator. Wir beraten Sie unverbindlich vor Ort.
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <NavLink to="/konfigurator" className="bg-white text-ms-solar px-12 py-6 rounded-2xl font-black text-2xl hover:bg-ms-light transition-all shadow-2xl uppercase tracking-tight gap-4">
              Konfigurator starten
            </NavLink>
            <NavLink to="/kontakt" className="bg-ms-red text-white px-12 py-6 rounded-2xl font-black text-2xl hover:opacity-90 transition-all shadow-2xl uppercase tracking-tight gap-4">
              Anfrage senden
            </NavLink>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;